import Icon from "assets/icons";
import React from "react";
import { useOutletContext } from "react-router-dom";
import Button from "shared/components/button";
import { useRegDetail } from "shared/context/regDetail";
import Size from "shared/enums/Size";
import { PaymentSuccessTransactionSubSet, TransactionStatus } from "shared/enums/TransactionStatus";
import Variant from "shared/enums/Variant";
import OpenLinkInNewTab from "shared/form/core/link";
import { useCopies } from "shared/hooks/useCopies";
import useDismissToast from "shared/hooks/useDismissToast";
import useWindowScrollTop from "shared/hooks/windowScrollTop";
import { RegFormContextType } from "../type";
import useRegConfirmationService from "./service";
import { PurchaseOrderTable } from "shared/components/purchaseOrder";
import ToolTip from "shared/components/tooltip";
import SocialMediaShareHandler from "../../../components/socialMediaShareHandler";
import FormTitle from "../../../components/formTitle";
import ParticipantBadgeDetails from "../../badgeDetails";
import RegistrationFooter from "apps/registration/components/registrationFooter";
import { ConfirmationTopNotification } from "../components";
import DateTimeHelper from "shared/utils/dateTimeHelper";
import useLogEventTriggerConfig from "../../../../../shared/hooks/useLogEventTriggerConfig";
import UtilityDataModule from "../../../../../shared/enums/UtilityDataModule";
import ContextEntityType from "../../../../../shared/enums/ContextEntityType";
import RegistrationStatus from "../../../../../shared/enums/RegistrationStatus";
import ApiPurchaseOrder from "../../../../../shared/model/ApiPurchaseOrder";

/*
  Event Name, WHERE (text), WHEN (text) are kept capital intentionally as users generally keep asking where and when the event is
  Hence to make it stand out these 3 things are displayed in upper case only
*/

/**
 * @todo SR Check for ticket transfer flow - to hide/show amount
 * @todo SR Confirmation Number checks
 * @todo SR send participant first name in sucess msg and helpDeskEmail
 * @todo SR remove hardcoding related to payments
 */

export default function RegConfirmation() {
  const { startDate, endDate, name, helpdeskEmail, offlinePaymentNote } = useRegDetail();
  const { participantObj } = useOutletContext<RegFormContextType>();
  const copies = useCopies();
  useWindowScrollTop();
  useDismissToast();
  const { triggerEvent } = useLogEventTriggerConfig({
    registrationId: participantObj.registrationId,
    module: UtilityDataModule.REGISTRATION_FORM_CONFIRMATION,
    contextEntityType: ContextEntityType.REGISTRATION_FORM,
    contextEntityId: participantObj?.registrationForm?.id
  });

  React.useEffect(() => {
    if (participantObj.registrationStatus === RegistrationStatus.REGISTERED) {
      triggerEvent({ retryCount: 2, dataObj: { email: participantObj.workEmail } });
    }
  }, [triggerEvent, participantObj.registrationStatus, participantObj.workEmail]);

  const regConfirmationService = useRegConfirmationService();

  return (
    <React.Fragment>
      <div className="regConfirmWrapper">
        <FormTitle title={participantObj?.registrationForm?.title} />
        <section className="container">
          <div className="purchaseConfirm">
            <div className="purchaseConfirm_wrapper">
              <div className="purchaseConfirm_tagsWrapper">
                <div className="purchaseConfirm_orderId">
                  {participantObj.regPurchaseOrder?.identifier && (
                    <p className="purchaseConfirm_orderId_text">
                      <span className="purchaseConfirm_orderId_text--label">
                        {copies.get("purchase.order.identifier")}
                      </span>
                      {participantObj.regPurchaseOrder.identifier}
                    </p>
                  )}
                </div>
                <div className="purchaseConfirm_status">
                  {participantObj.applicationFlowApprovalPending || participantObj.registrationFlowApprovalPending ? (
                    <p className="purchaseConfirm_status--pendingReview">{copies.get("pending.review")}</p>
                  ) : regConfirmationService.isPaymentReceiptPending ? (
                    <p className="purchaseConfirm_status--pendingReview">{copies.get("pending.receipt")}</p>
                  ) : (
                    PaymentSuccessTransactionSubSet.includes(
                      participantObj?.regPurchaseOrder?.transaction?.status as TransactionStatus
                    ) && <p className="purchaseConfirm_status--paymentReceived">{copies.get("payment.received")}</p>
                  )}
                </div>
              </div>

              <MsgWrapper
                participantName={`${participantObj.firstName} ${participantObj.lastName}`}
                isParticipantRegistered={participantObj.participantRegisteredAndTicketNotPending}
                isApplicationSubmitted={participantObj.applicationFlowApprovalPending}
                isRegistrationApprovalPending={participantObj.registrationFlowApprovalPending}
                isBulkTicketAdmin={regConfirmationService.bulkTicketAdmin}
                name={name}
                helpdeskEmail={helpdeskEmail}
                participantEmailId={participantObj.workEmail}
                forwardedToInvoicePayerAndPaymentPending={
                  regConfirmationService.forwardedToInvoicePayerAndPaymentPending
                }
                isPaymentReceiptPending={regConfirmationService.isPaymentReceiptPending}
              />
              <div className="purchaseConfirm_cardWrapper">
                <div className="purchaseConfirm_cardBody">
                  <div className="purchaseConfirm_cardLeft">
                    <h2 className="purchaseConfirm_cardLeft--regName">{name?.toUpperCase()}</h2>
                    <span className="purchaseConfirm_cardLeft--regDate">
                      <Icon.Date />
                      {DateTimeHelper.formatDate({ startDate, endDate })}
                    </span>
                  </div>
                  <div className="purchaseConfirm_cardRight">
                    <div className="purchaseConfirm_cardRight_top">
                      <label className="purchaseConfirm_cardRight_top--label">
                        {copies.get(
                          regConfirmationService.forwardedToInvoicePayerAndPaymentPending ||
                            regConfirmationService.isPaymentReceiptPending
                            ? "registration.email.label"
                            : "registered.email"
                        )}
                      </label>
                      <p className="purchaseConfirm_cardRight_top--value">{participantObj.workEmail}</p>
                    </div>
                    <div className="purchaseConfirm_cardRight_bottom">
                      <label className="purchaseConfirm_cardRight_bottom--label">
                        {participantObj.participantRegisteredAndTicketNotPending && (
                          <React.Fragment>
                            {copies.get("confirmation.number")}
                            <ToolTip content={copies.get("confirmation.number.info")} size={Size.SMALL} />
                          </React.Fragment>
                        )}
                        {(participantObj.applicationFlowApprovalPending ||
                          participantObj.registrationFlowApprovalPending ||
                          regConfirmationService.isPaymentReceiptPending) && (
                          <React.Fragment>{copies.get("application.status")}</React.Fragment>
                        )}
                        {regConfirmationService.forwardedToInvoicePayerAndPaymentPending &&
                          regConfirmationService.transactionDetails?.invoicePayerEmail && (
                            <React.Fragment>{copies.get("invoice.payment.requested.to.label")}</React.Fragment>
                          )}
                      </label>
                      <p className="purchaseConfirm_cardRight_bottom--value">
                        {participantObj.participantRegisteredAndTicketNotPending && (
                          <React.Fragment>{participantObj.confirmationNumber}</React.Fragment>
                        )}
                        {(participantObj.applicationFlowApprovalPending ||
                          participantObj.registrationFlowApprovalPending) && (
                          <React.Fragment>{copies.get("pending.review")}</React.Fragment>
                        )}
                        {regConfirmationService.isPaymentReceiptPending && (
                          <React.Fragment>{copies.get("pending.receipt")}</React.Fragment>
                        )}
                        {regConfirmationService.forwardedToInvoicePayerAndPaymentPending &&
                          regConfirmationService.transactionDetails?.invoicePayerEmail && (
                            <React.Fragment>
                              {regConfirmationService.transactionDetails?.invoicePayerEmail}
                            </React.Fragment>
                          )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {(participantObj?.regPurchaseOrder?.amount?.value || 0) > 0 && (
                <React.Fragment>
                  <PurchaseOrderTable
                    purchaseOrder={participantObj?.regPurchaseOrder as ApiPurchaseOrder}
                    originatingFormName={participantObj?.registrationForm?.title}
                    customFooter={
                      PaymentSuccessTransactionSubSet.includes(
                        participantObj?.regPurchaseOrder?.transaction?.status as TransactionStatus
                      ) && participantObj?.regPurchaseOrder?.invoicePaymentRequest?.invoicePayerEmail ? (
                        <span className="purchaseOrderTable_invoicePaidBy">
                          <Icon.CheckCircle />
                          <span
                            dangerouslySetInnerHTML={{
                              __html: copies.get("invoice.paid.by.message", {
                                0: `<strong class="purchaseOrderTable_invoicePaidBy--email">${participantObj?.regPurchaseOrder?.invoicePaymentRequest?.invoicePayerEmail}</strong>`
                              })
                            }}
                          />
                        </span>
                      ) : regConfirmationService.isPaymentReceiptPending && offlinePaymentNote ? (
                        <span className="purchaseOrderTable_offlinePaymentDetails printHide">
                          {copies.get("offline.payment.account.details.info.label")}
                          <ToolTip content={offlinePaymentNote} isHTMLFormatted>
                            <Button
                              label={copies.get("view")}
                              variant={Variant.LINK}
                              className="purchaseOrderTable_offlinePaymentDetails--btn"
                            />
                          </ToolTip>
                        </span>
                      ) : undefined
                    }
                  />
                </React.Fragment>
              )}
              {(participantObj?.regPurchaseOrder?.transaction?.status === TransactionStatus.PRE_AUTHORIZED ||
                regConfirmationService.forwardedToInvoicePayerAndPaymentPending ||
                regConfirmationService.isPaymentReceiptPending) && (
                <div className="purchaseConfirm_footerMsg">
                  <p className="purchaseConfirm_footerMsg--text">
                    <Icon.InfoCircleThick />
                    {participantObj?.regPurchaseOrder?.transaction?.status === TransactionStatus.PRE_AUTHORIZED && (
                      <React.Fragment>{copies.get("payment.will.be.processed")}</React.Fragment>
                    )}
                    {regConfirmationService.forwardedToInvoicePayerAndPaymentPending && (
                      <React.Fragment>
                        {copies.get("invoice.payment.registration.will.be.confirmed.on.payment")}
                      </React.Fragment>
                    )}
                    {regConfirmationService.isPaymentReceiptPending && (
                      <React.Fragment>
                        {copies.get("offline.payment.registration.will.be.confirmed.on.approval")}
                      </React.Fragment>
                    )}
                  </p>
                </div>
              )}
            </div>
            <div className="printHide purchaseConfirm_btnWrapper">
              <Button
                className="purchaseConfirm_btnWrapper--print"
                variant={Variant.PRIMARY}
                icon={<Icon.Print />}
                label={copies.get("print.confirmation")}
                isOutline
                onClick={() => {
                  window.scrollTo(0, 0);
                  window.print();
                }}
              />
              {participantObj.participantRegisteredAndTicketNotPending && participantObj.myExperienceLoginUrl && (
                <OpenLinkInNewTab
                  url={participantObj.myExperienceLoginUrl}
                  title={
                    regConfirmationService.bulkTicketAdmin
                      ? copies.get("manage.bulk.ticket")
                      : copies.get("manage.ticket")
                  }>
                  <Button
                    className="purchaseConfirm_btnWrapper--manageTicket"
                    variant={Variant.PRIMARY}
                    icon={<Icon.ManageTicket />}
                    label={
                      regConfirmationService.bulkTicketAdmin
                        ? copies.get("manage.bulk.ticket")
                        : copies.get("manage.ticket")
                    }
                    isOutline
                  />
                </OpenLinkInNewTab>
              )}
              <SocialMediaShareHandler participantObj={participantObj} />
              {participantObj.badgeSyncApplicable && !regConfirmationService.showBadgeDetails && (
                <Button
                  className="purchaseConfirm_btnWrapper--badge"
                  variant={Variant.PRIMARY}
                  icon={<Icon.Badge />}
                  label={copies.get("get.badge.details")}
                  isOutline
                  onClick={() => {
                    regConfirmationService.setShowBadgeDetails(true);
                  }}
                />
              )}
            </div>
            {regConfirmationService.isPaymentReceiptPending && offlinePaymentNote && (
              <div className="purchaseOrderTable_offlinePaymentDetailsWrapper printShow">
                <span className="purchaseOrderTable_offlinePaymentDetails_printOnly printShow">
                  <span className="purchaseOrderTable_offlinePaymentDetails_printOnly--label">
                    {copies.get("offline.payment.account.details.info.label")}
                  </span>
                  <p
                    className="purchaseOrderTable_offlinePaymentDetails_printOnly--info"
                    dangerouslySetInnerHTML={{ __html: offlinePaymentNote }}
                  />
                </span>
              </div>
            )}
          </div>
        </section>
        {regConfirmationService.showBadgeDetails && (
          <ParticipantBadgeDetails registrationParticipantId={participantObj.id} />
        )}
      </div>
      <RegistrationFooter />
    </React.Fragment>
  );
}

function MsgWrapper({
  participantName,
  isParticipantRegistered,
  isApplicationSubmitted,
  isRegistrationApprovalPending,
  name,
  helpdeskEmail,
  participantEmailId,
  isBulkTicketAdmin,
  isPaymentReceiptPending,
  forwardedToInvoicePayerAndPaymentPending
}: {
  participantName?: string;
  isParticipantRegistered: boolean;
  isApplicationSubmitted: boolean;
  isRegistrationApprovalPending: boolean;
  name?: string;
  helpdeskEmail?: string;
  participantEmailId?: string;
  isBulkTicketAdmin: boolean;
  isPaymentReceiptPending: boolean;
  forwardedToInvoicePayerAndPaymentPending?: boolean;
}) {
  const copies = useCopies();

  return (
    <React.Fragment>
      <div className="purchaseConfirm_icon">
        <Icon.CheckMarkOutlineSlim />
      </div>
      <div className="purchaseConfirm_msgWrapper">
        <div className="purchaseConfirm_msgHeading">
          <h3 className="purchaseConfirm_msgHeading--title">
            {isParticipantRegistered && (
              <React.Fragment>
                {copies.get("congratulations")},
                <strong className="purchaseConfirm_msgHeading--title_username">{participantName}</strong>
              </React.Fragment>
            )}
            {forwardedToInvoicePayerAndPaymentPending &&
              copies.get("invoice.payment.application.submitted.for.payment")}
            {(isApplicationSubmitted || isRegistrationApprovalPending || isPaymentReceiptPending) &&
              copies.get("thank.you")}
          </h3>
        </div>
        <div className="purchaseConfirm_msgDetails">
          {isParticipantRegistered && (
            <ConfirmationTopNotification
              isBTA={isBulkTicketAdmin}
              btaCopy={copies.get("bulk.ticket.purchase.successful", {
                0: name
              })}
              userCopy={copies.get("registration.successful.confirmation.email.sent", { 0: participantEmailId })}
            />
          )}
          {/* when payment is requested to invoice payer & is pending */}
          {forwardedToInvoicePayerAndPaymentPending && (
            <ConfirmationTopNotification
              isBTA={isBulkTicketAdmin}
              btaCopy={copies.get("invoice.payment.registration.submitted.by.bta", { 0: name })}
              userCopy={copies.get("invoice.payment.registration.submitted", { 0: name })}
              helpdeskEmail={helpdeskEmail}
            />
          )}
          {/* post payment/ when review is pending */}
          {(isApplicationSubmitted || isRegistrationApprovalPending) && (
            <ConfirmationTopNotification
              isBTA={isBulkTicketAdmin}
              btaCopy={copies.get("bulk.ticket.application.pending.review", {
                0: name,
                1: name
              })}
              userCopy={copies.get("application.pending.review", {
                0: name,
                1: name
              })}
              helpdeskEmail={helpdeskEmail}
            />
          )}
          {/* when offline payment mode is chosen */}
          {isPaymentReceiptPending && (
            <ConfirmationTopNotification
              isBTA={isBulkTicketAdmin}
              btaCopy={copies.get("bulk.ticket.application.pending.receipt", {
                0: name,
                1: name
              })}
              userCopy={copies.get("application.pending.receipt", {
                0: name,
                1: name
              })}
              helpdeskEmail={helpdeskEmail}
            />
          )}
        </div>
      </div>
    </React.Fragment>
  );
}
